import React, { Component } from 'react'
import * as Yup from 'yup'
import { withFormik } from 'formik'
import { Grid } from '@material-ui/core'

// components
import { Message, Modal } from '../../../../components'
import {
  RecordDetail,
  NameDetail,
  ContactInformation,
  Jobs,
  ManagedFunds,
  Biography,
  WebSource,
  Notes
} from './components'

import { difference } from 'lodash'

/**
 * Confirmation Modal
 * @param onClose
 * @param title
 * @param message
 * @param onDeleteConfirm
 * @returns {*}
 * @constructor
 */
const ConfirmModal = ({ onClose, title, message, onConfirm }) => {
  return (
    <Message
      visible
      type='warning'
      title={title}
      message={message}
      onClose={onClose}
      buttons={[
        {
          theme: 'shaded',
          label: 'cancel',
          onClick: onClose
        },
        {
          theme: 'citrus',
          label: 'confirm',
          onClick: () => onConfirm()
        }
      ]} />
  )
}

/**
 * Contact Form Component
 */
class ContactForm extends Component {
  /**
   * Constructor
   * @param props
   */
  constructor(props) {
    super(props)
    this.state = {
      gdpr: [],
      initialGDPR: null
    }
  }

  /**
   * Component Did Update
   * @param prevProps
   */
  componentDidUpdate (prevProps) {
    const { values } = this.props
    const { initialGDPR } = this.state

    if (prevProps.values.gdpr !== values.gdpr) {
      this.setState({ gdpr: values.gdpr })

      if (!prevProps.values.gdpr && !initialGDPR) {
        this.setState({ initialGDPR: values.gdpr || [] })
      }
    }
  }

  /**
   * Handle partial GDPR fields change
   * @param field
   */
  handleGDPRChange = (field) => {
    const { setFieldValue } = this.props
    const { gdpr } = this.state
    setFieldValue('gdpr', (gdpr || []).includes(field) ? gdpr.filter((item) => item !== field) : gdpr.concat(field))
  }

   /**
   * On Submit
   */
  onSubmitRequest () {
    const { values, handleSubmit, openModal } = this.props
    const { gdpr, initialGDPR } = this.state
    if (values.status === 'gdpr') {
      // gdpr confirm modal
      openModal({
        type: 'GDPR_WARNING',
        props: {
          data: {
            name: values.fullName || [values.firstName, values.middleName1, values.middleName2, values.lastName]
              .filter((name) => name && name.trim()).join(' '),
            jobs: values.jobs,
            status: values.status
          },
          onConfirm: this.onGDPRConfirm.bind(this)
        }
      })

    } else if ((gdpr !== initialGDPR) && difference(gdpr, initialGDPR).length) {
      // partial gdpr confirm modal
      openModal({
        component: ({ onClose }) => ConfirmModal({
          onClose,
          title: 'GDPR Fields',
          message: 'You have flagged one or more fields as GDPR. Do you wish to proceed?',
          onConfirm: this.onPartialGDPRConfirm.bind(this)
        })
      })

    } else {
      handleSubmit && handleSubmit()
    }

  }

  /**
   * Confirm GDPR status and set all values for full GDPR removal
   */
  onGDPRConfirm () {
    const { values, setValues, handleSubmit } = this.props

    setValues({
      ...values,
      status: 'gdpr',
      salutation: '',
      suffixProf: '',
      nickName: '',
      email: '',
      directPhone: '',
      phoneExtension: '',
      mobile: '',
      linkedIn: '',
      website1: '',
      website2: '',
      fca: '',
      sec: '',
      finra: '',
      bio: '',
      jobs: [],
      strategyCoverage: '',
      managedFunds: [],
      gdpr: [],
      strategy: []
    })

    handleSubmit && handleSubmit()
  }

  /**
   * Confirm partial GDPR status and set field values
   */
  onPartialGDPRConfirm () {
    const { setFieldValue, handleSubmit } = this.props
    const { gdpr = [] } = this.state

    this._handleMessageClose()

    if (gdpr.includes('directPhone')) {
      setFieldValue('phoneExtension', '')
    }

    (gdpr || []).forEach((item) => setFieldValue(item, ''))

    handleSubmit && handleSubmit()
  }

  /**
   * Handle close message modal
   * @private
   */
  _handleMessageClose () {
    const { closeModal } = this.props
    closeModal({
      component: ConfirmModal
    })
  }

  /**
   * Render
   * @return {XML}
   */
  render () {
    const { gdpr } = this.state
    const {
      visible, loading, values, errors, setFieldValue, handleChange, onClose,
      searchInstitution, searchInstitutions, institutionLoading, institutions,
      searchFunds, fundLoading, funds, clearSuggestions, openModal, closeModal
    } = this.props

    const buttons = [
      {
        label: 'Cancel',
        ui: 'shaded',
        onClick: onClose
      },
      {
        label: 'Create Change Request',
        ui: 'citrus',
        onClick: this.onSubmitRequest.bind(this)
      }
    ]

    return (
      <Modal
        visible={visible}
        loading={loading}
        fullscreen
        badge='q4i-contact-2pt'
        title={values.id ? 'Edit Contact' : 'Create Contact'}
        buttons={buttons}
        onClose={onClose}>

        <Grid container spacing={3}>

          <RecordDetail
            values={values}
            setFieldValue={setFieldValue} />

          <NameDetail
            errors={errors}
            values={values}
            onChange={handleChange} />

          <ContactInformation
            errors={errors}
            values={values}
            onChange={handleChange}
            onCheck={this.handleGDPRChange}
            gdpr={gdpr} />

          <Jobs
            values={values}
            searchInstitution={searchInstitution}
            searchInstitutions={searchInstitutions}
            loading={institutionLoading}
            suggestions={institutions}
            clearSuggestions={clearSuggestions}
            setFieldValue={setFieldValue}
            openModal={openModal}
            closeModal={closeModal} />

          <ManagedFunds
            jobs={values.jobs}
            managedFunds={values.managedFunds}
            searchFunds={searchFunds}
            loading={fundLoading}
            suggestions={funds}
            clearSuggestions={clearSuggestions}
            setFieldValue={setFieldValue} />

          <Biography
            values={values}
            onChange={handleChange} />

          <WebSource
            values={values}
            onChange={handleChange} />

          <Notes
            values={values}
            onChange={handleChange} />

        </Grid>
      </Modal>
    )
  }
}

export default withFormik({
  enableReinitialize: true,
  validateOnChange: false,
  validateOnBlur: false,
  mapPropsToValues: (props) => {
    return {
      id: '',
      source: '',
      status: '',
      salutation: '',
      suffixProf: '',
      firstName: '',
      lastName: '',
      fullName: '',
      nickName: '',
      email: '',
      directPhone: '',
      phoneExtension: '',
      mobile: '',
      linkedIn: '',
      website1: '',
      website2: '',
      fca: '',
      sec: '',
      finra: '',
      bio: '',
      notes: '',
      jobs: [],
      strategyCoverage: '',
      managedFunds: [],
      gdpr: [],
      strategy: [],
      ...props.values
    }
  },
  handleSubmit: (values, { props }) => {
    const { onSaveChangeRequest } = props
    onSaveChangeRequest && onSaveChangeRequest(values)
  },
  validationSchema: Yup.object().shape({
    email: Yup.string().trim().email('Invalid email address'),
    firstName: Yup.string().trim().required(),
    lastName: Yup.string().trim().required()
  })
})(ContactForm)
